import { useCallback, useEffect, useRef, useState } from "react";
import { ZodError } from "zod";
import { apiRequest } from "../../shared/api/client";
import { week39PackagingLiveSchema } from "./week39-live-schema";
import type { Week39PackagingLive, Week39PackagingSnapshot } from "./week39-types";

const REFRESH_INTERVAL_MS = 30 * 60_000;

export function useWeek39PackagingLive(snapshot: Week39PackagingSnapshot | null) {
  const [live, setLive] = useState<Week39PackagingLive | null>(null);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const requestId = useRef(0);
  const activeRequest = useRef<AbortController | null>(null);
  const lastAttemptAt = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    if (!snapshot || activeRequest.current) return;
    const id = ++requestId.current;
    const controller = new AbortController();
    activeRequest.current = controller;
    lastAttemptAt.current = Date.now();
    setRefreshing(true);
    try {
      const response = await apiRequest<unknown>(`/api/sarf39/manual-packaging/live?weekStart=${snapshot?.weekStart ?? ""}`, { cache: "no-store", signal: controller.signal });
      const result = week39PackagingLiveSchema.parse(response);
      if (id !== requestId.current) return;
      setLive(result);
      setError("");
    } catch (reason) {
      if (id !== requestId.current) return;
      setLive(null);
      setError(reason instanceof ZodError ? "Mikro yanıtı doğrulanamadı; gerçekleşme gösterilmiyor."
        : reason instanceof Error ? reason.message : "Mikro verileri alınamadı.");
    } finally {
      if (activeRequest.current === controller) activeRequest.current = null;
      if (id === requestId.current) setRefreshing(false);
    }
  }, [snapshot]);

  useEffect(() => {
    if (!snapshot) return;
    void refresh();
    const refreshIfDue = () => {
      if (document.visibilityState === "visible"
        && (lastAttemptAt.current === null || Date.now() - lastAttemptAt.current >= REFRESH_INTERVAL_MS)) void refresh();
    };
    const timer = window.setInterval(refreshIfDue, REFRESH_INTERVAL_MS);
    const onVisible = refreshIfDue;
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      requestId.current += 1;
      activeRequest.current?.abort();
      activeRequest.current = null;
    };
  }, [snapshot, refresh]);

  return { live, error, refreshing, refresh };
}
